import { useMemo, useState } from "react";
import { InputText } from "primereact/inputtext";
import { Combobox, formatCurrency, formatDate, getDate } from "medere-ui-framework";
import {
    BudgetDetailDTO,
    defaultDisposablesBudgetDetailDTO,
    defaultExtrasBudgetDetailDTO,
    defaultMedicinesBudgetDetailDTO,
    defaultNutritionalSupportBudgetDetailDTO,
    withDefaultCoveragePercentages,
} from "../../../dto/budget/BudgetDetailDTO";
import { ComboDataDTO } from "../../../dto/ComboDataDTO";
import { ComboMPPDTO } from "../../../dto/budget/ComboMPPDTO";
import { StockItemPresentationValueDTO } from "../../../dto/budget/StockItemPresentationValueDTO";
import {
    getDisposablesStockItemsForCombo,
    getMedicines,
    getNutritionalSupport,
    getStockItemPresentationValue,
} from "../../../servicesHooks/BillHospitalization/useBillHospitalizationService";
import {
    BILL_ITEM_CATEGORY_CONFIG,
    BillItemCategory,
    amountOf,
    canEditQuantity,
    getBillableUnits,
    getCategorySubtotal,
    getUnitsLabel,
    recalculateItem,
    toUnits,
    toWholeUnits,
    withQuantity,
    withUnitPrice,
} from "./billItems";
import LoadTable, { LoadTableColumn, LoadTableRow } from "../../../components/novarium/LoadTable";
import NumberField from "./NumberField";
import { AddRowButton, FieldError, RowActions, formatApplicationDate, formatCoverage } from "./tableParts";

export type SupplyCategory = Extract<BillItemCategory, 'disposables' | 'nutritionalSupportItems' | 'medicines' | 'extras'>;

type Row = LoadTableRow & { item: BudgetDetailDTO; index: number };

interface SupplyTabProps {
    category: SupplyCategory;
    items: BudgetDetailDTO[];
    onChange: (items: BudgetDetailDTO[]) => void;
    onEdit: (index: number) => void;
}

interface AddDraft {
    /** Texto del combo o, en Extras, la descripción escrita. */
    text: string;
    /** Campos del ítem elegido en el combo (id, descripción, presentación). */
    picked: Partial<BudgetDetailDTO> & Record<string, unknown> | null;
    priceDate: Date | null;
    units: number | null;
    price: number | null;
    /** El valor unitario lo escribió la persona: el precio del ítem no lo pisa. */
    priceEdited: boolean;
    errors: { item?: string; units?: string; price?: string };
}

const emptyDraft = (): AddDraft => ({ text: '', picked: null, priceDate: null, units: 1, price: null, priceEdited: false, errors: {} });

const DEFAULT_ITEM: Record<SupplyCategory, BudgetDetailDTO> = {
    disposables: defaultDisposablesBudgetDetailDTO,
    nutritionalSupportItems: defaultNutritionalSupportBudgetDetailDTO,
    medicines: defaultMedicinesBudgetDetailDTO,
    extras: defaultExtrasBudgetDetailDTO,
};

/** Precio por unidad de un medicamento o fórmula: el costo del envase dividido sus unidades. */
const unitPriceFromPresentation = (e: ComboMPPDTO): number =>
    e.unitsperpackage ? (e.cost ?? 0) / e.unitsperpackage : (e.cost ?? 0);

export default function SupplyTab({ category, items, onChange, onEdit }: SupplyTabProps) {
    const config = BILL_ITEM_CATEGORY_CONFIG[category];
    const [draft, setDraft] = useState<AddDraft>(emptyDraft);
    const idPrefix = `${category}-add`;
    // Medicamentos y soporte nutricional se pueden cobrar en proporción a la dosis; descartables y extras van enteros
    const allowsFractions = category === 'medicines' || category === 'nutritionalSupportItems';
    const parseUnits = allowsFractions ? toUnits : toWholeUnits;
    const unitsKind = allowsFractions ? 'decimal' : 'integer';

    const rows: Row[] = useMemo(() => items.map((item, index) => ({ rowKey: `${index}-${item.description}`, item, index })), [items]);

    const update = (index: number, item: BudgetDetailDTO) =>
        onChange(items.map((current, i) => i === index ? recalculateItem(category, item) : current));

    const clearItemError = (prev: AddDraft) => ({ ...prev.errors, item: undefined });

    const pickDisposable = async (e: ComboDataDTO | null) => {
        if (!e?.id) {
            setDraft(prev => ({ ...prev, text: e?.name ?? '', picked: null }));
            return;
        }
        setDraft(prev => ({ ...prev, text: e.name ?? '', picked: { item: e.id, description: e.name ?? '' }, priceEdited: false, errors: clearItemError(prev) }));
        const presentation: StockItemPresentationValueDTO | null = await getStockItemPresentationValue(e.id).catch(() => null);
        setDraft(prev => prev.picked?.item === e.id
            ? { ...prev, price: prev.priceEdited ? prev.price : presentation?.price ?? 0, priceDate: presentation?.pricesettingdate ? getDate(presentation.pricesettingdate) : null, picked: { ...prev.picked, pricesettingdate: presentation?.pricesettingdate ?? null } }
            : prev);
    };

    const pickPresentation = (e: ComboMPPDTO | null) => {
        if (!e?.id) {
            setDraft(prev => ({ ...prev, text: e?.name ?? '', picked: null }));
            return;
        }
        setDraft(prev => ({
            ...prev,
            text: e.name ?? '',
            picked: { description: e.substanceandpresentation ?? e.name ?? '', comboMPPDTO: e },
            price: unitPriceFromPresentation(e),
            priceEdited: false,
            errors: clearItemError(prev),
        }));
    };

    const add = () => {
        const errors: AddDraft['errors'] = {};
        const isExtra = category === 'extras';
        if (isExtra ? !draft.text.trim() : !draft.picked) {
            errors.item = isExtra ? 'Escribí el extra' : `Elegí ${config.article} ${config.itemLabel}`;
        }
        const units = parseUnits(draft.units);
        if (units <= 0) errors.units = 'Ingresá las unidades';
        if (!draft.price || draft.price <= 0) errors.price = 'Ingresá el valor unitario';
        if (errors.item || errors.units || errors.price) {
            setDraft(prev => ({ ...prev, errors }));
            return;
        }
        const base = {
            ...DEFAULT_ITEM[category],
            ...(isExtra ? { description: draft.text.trim() } : draft.picked),
            unitsqty: units,
        } as BudgetDetailDTO;
        const item = withDefaultCoveragePercentages(withUnitPrice(category, base, draft.price!));
        onChange([...items, recalculateItem(category, item)]);
        setDraft(emptyDraft());
    };

    const draftTotal = (draft.picked || draft.text.trim()) && draft.price
        ? formatCurrency(amountOf(parseUnits(draft.units), draft.price).toNumber())
        : '—';

    const itemPicker = (() => {
        const common = {
            optionLabel: 'name',
            placeholder: 'Buscá por nombre',
            minPanelWidth: 500,
            maxPanelWidth: 800,
            value: draft.text,
            forceSelection: false,
            error: Boolean(draft.errors.item),
            caption: draft.errors.item,
        };
        switch (category) {
            case 'disposables':
                return <Combobox {...common} getItems={getDisposablesStockItemsForCombo} setValue={(e: ComboDataDTO | null) => { void pickDisposable(e); }} />;
            case 'nutritionalSupportItems':
                return <Combobox {...common} getItems={getNutritionalSupport} setValue={pickPresentation} />;
            case 'medicines':
                return <Combobox {...common} getItems={getMedicines} setValue={pickPresentation} />;
            case 'extras':
                return (
                    <>
                        <InputText
                            aria-label="Extra nuevo"
                            placeholder="Ej.: Traslado en ambulancia"
                            value={draft.text}
                            onChange={(e) => setDraft(prev => ({ ...prev, text: e.target.value, errors: clearItemError(prev) }))}
                            className={draft.errors.item ? 'p-invalid' : undefined}
                            aria-invalid={Boolean(draft.errors.item) || undefined}
                            aria-describedby={`${idPrefix}-item-error`}
                        />
                        <FieldError id={`${idPrefix}-item-error`} message={draft.errors.item} />
                    </>
                );
        }
    })();

    const columns: LoadTableColumn<Row>[] = [
        {
            key: 'item',
            header: config.itemHeader,
            className: 'nv-col-item',
            addCell: <div className="nv-cell-field">{itemPicker}</div>,
            body: ({ item }) => <span className="nv-truncate nv-tip" data-pr-tooltip={item.description}>{item.description}</span>,
        },
        {
            key: 'date',
            header: 'Fecha',
            align: 'center',
            className: 'nv-col-date',
            addCell: <span className="nv-muted">—</span>,
            body: ({ item }) => formatApplicationDate(item.applicationdate),
        },
        {
            key: 'units',
            header: config.quantityHeader,
            align: 'center',
            className: 'nv-col-qty',
            addCell: (
                <div className="nv-cell-field">
                    <NumberField kind={unitsKind} inCell ariaLabel={`Unidades del ítem nuevo`} value={draft.units} min={0}
                        invalid={Boolean(draft.errors.units)} describedBy={`${idPrefix}-units-error`}
                        onCommit={(units) => setDraft(prev => ({ ...prev, units, errors: { ...prev.errors, units: undefined } }))} />
                    <FieldError id={`${idPrefix}-units-error`} message={draft.errors.units} />
                </div>
            ),
            body: ({ item, index }) => {
                if (canEditQuantity(category, item)) {
                    return (
                        <NumberField kind={unitsKind} inCell ariaLabel={`Unidades de ${item.description}`} value={getBillableUnits(item)} min={0}
                            onCommit={(units) => update(index, withQuantity(category, item, units))} />
                    );
                }
                const units = getUnitsLabel(item);
                return <span className="nv-tip" data-pr-tooltip={`${units.detail}. Se cambian desde Editar ${config.itemLabel}.`}>{units.text}</span>;
            },
        },
        {
            key: 'price',
            header: 'Valor unitario',
            align: 'right',
            className: 'nv-col-money',
            addCell: (
                <div className="nv-cell-field">
                    <NumberField kind="currency" inCell ariaLabel={`Valor unitario del ítem nuevo`} value={draft.price} min={0}
                        invalid={Boolean(draft.errors.price)} describedBy={`${idPrefix}-price-error`}
                        onCommit={(price) => setDraft(prev => ({ ...prev, price, priceEdited: true, errors: { ...prev.errors, price: undefined } }))} />
                    <FieldError id={`${idPrefix}-price-error`} message={draft.errors.price} />
                    {draft.priceDate && <small className="nv-help">Actualizado el {formatDate(draft.priceDate)}</small>}
                </div>
            ),
            body: ({ item, index }) => (
                <NumberField kind="currency" inCell ariaLabel={`Valor unitario de ${item.description}`} value={item.unitprice} min={0}
                    onCommit={(price) => update(index, withUnitPrice(category, item, price))} />
            ),
        },
        {
            key: 'coverage',
            header: 'Cobertura OS',
            align: 'center',
            className: 'nv-col-pct',
            addCell: <span className="nv-muted">—</span>,
            body: ({ item }) => formatCoverage(item.healthentitypercentagecoverage),
        },
        {
            key: 'total',
            header: 'Total',
            align: 'right',
            className: 'nv-col-money',
            addCell: <span className="nv-strong">{draftTotal}</span>,
            body: ({ item }) => <span className="nv-strong">{formatCurrency(item.valuemonth ?? 0)}</span>,
        },
    ];

    return (
        <LoadTable
            rows={rows}
            columns={columns}
            addAction={<AddRowButton label={`Agregar ${config.itemLabel}`} onClick={add} />}
            rowActions={({ item, index }) => (
                <RowActions
                    itemName={item.description}
                    editLabel={`Editar ${config.itemLabel}`}
                    onEdit={() => onEdit(index)}
                    onRemove={() => onChange(items.filter((_, i) => i !== index))}
                />
            )}
            emptyMessage={config.emptyMessage}
            footerValues={{ total: formatCurrency(getCategorySubtotal(items).toNumber()) }}
        />
    );
}
